import { Controller } from "@hotwired/stimulus";

// Fired by Turbo/idiomorph on every element it reconciles during a morph refresh, target = the
// element that was morphed. Bubbles, so listening on the host element and filtering by `target`
// scopes this to the controller's own instance even when several selects share a page.
const MORPH_ELEMENT_EVENT = "turbo:morph-element";

// Restores the bound value onto a native <select>. The generic <form-select> component opts out of
// ASP.NET's SelectTagHelper (so callers keep full control over the <option> markup), which means the
// server no longer marks the matching option as selected. We apply it on the client from the model
// value instead. Empty value, or a value that is not among the <option>s, leaves the browser default
// (first option). Do not assign a value the select cannot hold — that unselects every option while
// the first label still looks selected.
//
// Morph-aware: a Turbo morph refresh (e.g. a refused form save posting back to its own URL) keeps
// this <select> element in place and idiomorph reconciles its <option> children, which resets the
// element back to the first option. Because the element survives, Stimulus never re-runs connect(),
// so this re-applies the bound value on the `turbo:morph-element` event too. Same problem and same
// fix as `rich-text-editor-controller`.
export default class extends Controller {
   static values = { selected: String };

   declare readonly selectedValue: string;
   declare readonly hasSelectedValue: boolean;

   private readonly handleMorphElement = (event: Event): void => {
      if (event.target !== this.element) return;
      this.applySelectedValue();
   };

   connect() {
      this.element.addEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
      this.applySelectedValue();
   }

   disconnect() {
      this.element.removeEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
   }

   private applySelectedValue(): void {
      if (!this.hasSelectedValue || this.selectedValue === "") {
         return;
      }

      const select = this.element as HTMLSelectElement;
      const boundValueIsAmongOptions = Array.from(select.options).some((option) => option.value === this.selectedValue);
      if (!boundValueIsAmongOptions) {
         return;
      }

      // Assign only when the value differs. Writing the same value again closes an open native
      // options list (same class of flash-close as a redundant focus() after morph).
      if (select.value !== this.selectedValue) {
         select.value = this.selectedValue;
      }
   }
}
